/**
 * Convert integers and MVR amounts to Dhivehi (Thaana) words.
 *
 * Ported from the PHP implementation, which uses the Maldivian
 * billion / million / lakh (ލައްކަ) / thousand structure and joins scale
 * words directly to the preceding number word (e.g. ފަސްހާސް).
 *
 * Example: 543.23 -> "ފަސްސަތޭކަ ސާޅީސް ތިންރުފިޔާ / ތޭވީސްލާރި"
 *
 * Supports values up to 999,999,999,999.99.
 */
const UNITS = [
    "",
    "އެއް",
    "ދެ",
    "ތިން",
    "ހަތަރު",
    "ފަސް",
    "ހަ",
    "ހަތް",
    "އަށް",
    "ނުވަ",
];
const TENS = [
    "",
    "ދިހަ",
    "ވިހި",
    "ތިރީސް",
    "ސާޅީސް",
    "ފަންސާސް",
    "ފަސްދޮޅަސް",
    "ހަތްދިހަ",
    "އައްޑިހަ",
    "ނުވަދިހަ",
];
/** Irregular names for 11–19 and 21–29, keyed by the actual number. */
const SPECIAL_NUMBERS = {
    11: "އެގާރަ",
    12: "ބާރަ",
    13: "ތޭރަ",
    14: "ސާދަ",
    15: "ފަނަރަ",
    16: "ސޯޅަ",
    17: "ސަތާރަ",
    18: "އަށާރަ",
    19: "ނަވާރަ",
    21: "އެކާވީސް",
    22: "ބާވީސް",
    23: "ތޭވީސް",
    24: "ސައުވީސް",
    25: "ފަންސަވީސް",
    26: "ސައްބީސް",
    27: "ހަތާވީސް",
    28: "އަށާވީސް",
    29: "ނަވާވީސް",
};
const ZERO = "ސުމެއް";
const TWO_HUNDRED = "ދުއިސައްތަ";
const SATHEYKA = "ސަތޭކަ";
const HAAS = "ހާސް";
const LAKKA = "ލައްކަ";
const MILLION = "މިލިއަން";
const BILLION = "ބިލިއަން";
const RUFIYAA = "ރުފިޔާ";
const LAARI = "ލާރި";
const MAX_SUPPORTED = 999_999_999_999;
/** Convert 1–99 to Dhivehi words. */
function underHundred(number) {
    if (number <= 0) {
        return "";
    }
    if (number < 10) {
        return UNITS[number] ?? "";
    }
    if (number === 10) {
        return TENS[1];
    }
    if (number === 20) {
        return TENS[2];
    }
    if (number in SPECIAL_NUMBERS) {
        return SPECIAL_NUMBERS[number] ?? "";
    }
    const tens = Math.floor(number / 10);
    const units = number % 10;
    const tensWord = TENS[tens] ?? "";
    return units > 0 ? `${tensWord} ${UNITS[units]}` : tensWord;
}
/** Convert 1–999 to Dhivehi words. */
function getHun(number) {
    if (number <= 0) {
        return "";
    }
    if (number > 999) {
        throw new RangeError("getHun() only supports numbers from 0 to 999.");
    }
    const parts = [];
    const hundreds = Math.floor(number / 100);
    const remainder = number % 100;
    if (hundreds > 0) {
        parts.push(hundreds === 2 ? TWO_HUNDRED : `${UNITS[hundreds]}${SATHEYKA}`);
    }
    if (remainder > 0) {
        parts.push(underHundred(remainder));
    }
    return parts.join(" ");
}
/**
 * Convert an integer up to 999,999,999,999 into Dhivehi words using the
 * billion / million / lakh / thousand structure. Returns "" for 0.
 */
function dv(value) {
    let number = value;
    if (number <= 0) {
        return "";
    }
    if (number > MAX_SUPPORTED) {
        throw new RangeError("Maximum supported value is 999,999,999,999.");
    }
    const parts = [];
    const billions = Math.floor(number / 1_000_000_000);
    if (billions > 0) {
        parts.push(`${getHun(billions)}${BILLION}`);
        number %= 1_000_000_000;
    }
    const millions = Math.floor(number / 1_000_000);
    if (millions > 0) {
        parts.push(`${getHun(millions)}${MILLION}`);
        number %= 1_000_000;
    }
    // Lakhs cover 100,000–900,000, so this is always a single digit (1–9).
    const lakhs = Math.floor(number / 100_000);
    if (lakhs > 0) {
        parts.push(`${UNITS[lakhs]}${LAKKA}`);
        number %= 100_000;
    }
    const thousands = Math.floor(number / 1_000);
    if (thousands > 0) {
        parts.push(`${getHun(thousands)}${HAAS}`);
        number %= 1_000;
    }
    if (number > 0) {
        parts.push(getHun(number));
    }
    return parts.join(" ");
}
/** Convert a non-negative integer to Dhivehi words. */
export function numberToDhivehi(n) {
    if (!Number.isFinite(n) || n < 0 || !Number.isInteger(n)) {
        throw new RangeError("numberToDhivehi must be a non-negative integer");
    }
    if (n === 0) {
        return ZERO;
    }
    return dv(n);
}
function toNumericAmount(amount) {
    if (typeof amount === "number") {
        return amount;
    }
    if (typeof amount === "string") {
        const parsed = parseFloat(amount.trim().replace(/,/g, ""));
        if (Number.isNaN(parsed)) {
            throw new RangeError("Invalid amount");
        }
        return parsed;
    }
    if (amount && typeof amount.toNumber === "function") {
        return amount.toNumber();
    }
    if (amount && typeof amount.toString === "function") {
        const parsed = parseFloat(amount.toString().replace(/,/g, ""));
        if (Number.isNaN(parsed)) {
            throw new RangeError("Invalid amount");
        }
        return parsed;
    }
    const parsed = Number(amount);
    if (Number.isNaN(parsed)) {
        throw new RangeError("Invalid amount");
    }
    return parsed;
}
/** Split an MVR amount into rufiyaa and laari (rounded to 2 dp). */
export function splitMvrAmount(amount) {
    const numeric = toNumericAmount(amount);
    if (numeric < 0) {
        throw new RangeError("Amount must be non-negative");
    }
    if (numeric > MAX_SUPPORTED + 0.99) {
        throw new RangeError("Maximum supported value is 999,999,999,999.99");
    }
    // Work in laari to avoid floating-point issues.
    const totalLaari = Math.round(numeric * 100);
    const laari = totalLaari % 100;
    return {
        rufiyaa: Math.floor(totalLaari / 100),
        laari,
        includeLaari: laari > 0,
    };
}
/**
 * Convert an MVR amount to Dhivehi words.
 *
 * Format: "<rufiyaa words>ރުފިޔާ <laari words>ލާރި", leaving out a zero part, e.g.:
 *
 *   543.23 -> "ފަސްސަތޭކަ ސާޅީސް ތިންރުފިޔާ ތޭވީސްލާރި"
 *   5000   -> "ފަސްހާސްރުފިޔާ"
 *   0.50   -> "ފަންސާސްލާރި"
 *   0      -> "-"
 */
export function currencyToDhivehiWords(amount) {
    const { rufiyaa, laari } = splitMvrAmount(amount);
    const parts = [];
    if (rufiyaa > 0)
        parts.push(`${dv(rufiyaa)}${RUFIYAA}`);
    if (laari > 0)
        parts.push(`${dv(laari)}${LAARI}`);
    return parts.length ? parts.join(" ") : "-";
}
//# sourceMappingURL=dhivehiCurrency.js.map